import { Controller } from "@hotwired/stimulus";

// Shared across desktop/tablet/mobile card shells so row selection survives resize.
const selectedKeys = new Set();
const SELECTION_CHANGED = "educator-home-ccai:selection-changed";
const METADATA_BADGE_SELECTOR =
  ".educator-home-page-calibrate-curricular-action-items-card__metadata-badges .badge-aq-chip";
const METADATA_BADGE_MULTILINE_CLASS =
  "educator-home-page-calibrate-curricular-action-items-card__metadata-badge--multiline";
let connectedInstanceCount = 0;
let boundBeforeCache = null;

function selectionKey(checkbox) {
  const documentId = checkbox?.dataset?.documentId;
  if (!documentId) return null;

  return `${documentId}::${checkbox.dataset.deliveryPurpose || ""}`;
}

function clearSelectionOnBeforeCache() {
  selectedKeys.clear();
  document.dispatchEvent(new CustomEvent(SELECTION_CHANGED));
}

export default class extends Controller {
  static targets = [
    "checkbox",
    "exportButton",
    "createCourseButton",
    "itemsField",
    "deliveryIdsField",
    "csrfTokenField",
    "successAlertTemplate",
  ];

  static values = {
    createCourseUrl: String,
    selectedDeliveryIds: { type: Array, default: [] },
    cardFrameId: String,
    earlyResultsFrameId: String,
    lateResultsFrameId: String,
  };

  static clearSelectionStore() {
    selectedKeys.clear();
    connectedInstanceCount = 0;
    if (boundBeforeCache) {
      document.removeEventListener("turbo:before-cache", boundBeforeCache);
      boundBeforeCache = null;
    }
  }

  connect() {
    connectedInstanceCount += 1;
    if (connectedInstanceCount === 1 && !boundBeforeCache) {
      boundBeforeCache = clearSelectionOnBeforeCache;
      document.addEventListener("turbo:before-cache", boundBeforeCache);
    }

    this.boundSelectionChanged = this.applySharedSelection.bind(this);
    this.boundFilterSubmit = this.clearSelectionOnFilterSubmit.bind(this);

    document.addEventListener(SELECTION_CHANGED, this.boundSelectionChanged);
    this.element.addEventListener("turbo:submit-start", this.boundFilterSubmit);

    this.checkboxTargets.forEach((checkbox) => {
      if (checkbox.checked) this.rememberSelection(checkbox);
    });
    this.applySharedSelection();
    const timingKeys = this.selectedIdKeyByTiming(this.filterForm());
    this.previousEarlyKey = timingKeys.early;
    this.previousLateKey = timingKeys.late;
    this.boundPrepareFilterSubmitSync =
      this.prepareFilterSubmitSync.bind(this);
    this.syncFilterFormSubmitListener();

    this.boundScheduleSyncMultilineMetadataBadges =
      this.scheduleSyncMultilineMetadataBadges.bind(this);
    this.element.addEventListener(
      "turbo:frame-load",
      this.boundScheduleSyncMultilineMetadataBadges,
    );
    if (typeof ResizeObserver !== "undefined") {
      this.metadataBadgeResizeObserver = new ResizeObserver((entries) => {
        const width = Math.round(entries[0]?.contentRect?.width || 0);
        if (width === this.metadataBadgeObservedWidth) return;

        this.metadataBadgeObservedWidth = width;
        this.scheduleSyncMultilineMetadataBadges();
      });
      this.metadataBadgeResizeObserver.observe(this.element);
    }
    this.scheduleSyncMultilineMetadataBadges();
  }

  scheduleSyncMultilineMetadataBadges() {
    if (this.metadataBadgeSyncFrame) return;

    this.metadataBadgeSyncFrame = requestAnimationFrame(() => {
      this.metadataBadgeSyncFrame = null;
      this.syncMultilineMetadataBadges();
    });
  }

  syncMultilineMetadataBadges() {
    this.element.querySelectorAll(METADATA_BADGE_SELECTOR).forEach((chip) => {
      const inner = chip.querySelector("span");
      if (!inner || chip.getBoundingClientRect().width === 0) {
        chip.classList.remove(METADATA_BADGE_MULTILINE_CLASS);
        return;
      }

      const range = document.createRange();
      range.selectNodeContents(inner);
      chip.classList.toggle(METADATA_BADGE_MULTILINE_CLASS, range.getClientRects().length > 1);
    });
  }

  prepareDeliveryFilterChange() {
    this.applyFilterScope({ deliveryFilter: true });
  }

  prepareSearchFilterChange() {
    this.applyFilterScope({ deliveryFilter: false });
  }

  prepareFilterSubmitSync(event) {
    const form = event.target;
    if (form?.tagName !== "FORM") return;

    const action = form.getAttribute("action") || "";
    if (!action.includes("calibrate_curricular_action_items_card/query")) return;

    this.applyFilterScope({
      deliveryFilter: document.activeElement?.name !== "search",
    });
  }

  syncFilterFormSubmitListener() {
    if (this.filterFormSubmitListenerForm) {
      this.filterFormSubmitListenerForm.removeEventListener(
        "submit",
        this.boundPrepareFilterSubmitSync,
        true,
      );
    }

    const form = this.filterForm();
    this.filterFormSubmitListenerForm = form;
    form?.addEventListener("submit", this.boundPrepareFilterSubmitSync, true);
  }

  applyFilterScope({ deliveryFilter }) {
    const form = this.filterForm();
    if (!form) return;

    let earlyChanged = false;
    let lateChanged = false;

    const timingKeys = this.selectedIdKeyByTiming(form);
    const noneSelected = !timingKeys.early && !timingKeys.late;

    if (deliveryFilter) {
      earlyChanged = timingKeys.early !== this.previousEarlyKey;
      lateChanged = timingKeys.late !== this.previousLateKey;
      this.previousEarlyKey = timingKeys.early;
      this.previousLateKey = timingKeys.late;
    }

    let turboFrame = form.dataset.turboFrame || this.cardFrameIdValue;
    let skeletonFrameIds = this.pendingSkeletonFrameIds();

    if (!deliveryFilter || noneSelected || (earlyChanged && lateChanged)) {
      turboFrame = this.cardFrameIdValue;
      skeletonFrameIds = [
        this.earlyResultsFrameIdValue,
        this.lateResultsFrameIdValue,
      ];
    } else if (earlyChanged) {
      turboFrame = this.earlyResultsFrameIdValue;
      skeletonFrameIds = [turboFrame];
    } else if (lateChanged) {
      turboFrame = this.lateResultsFrameIdValue;
      skeletonFrameIds = [turboFrame];
    }

    if (
      deliveryFilter &&
      !noneSelected &&
      skeletonFrameIds.length === 1 &&
      !this.element.querySelector(`[id="${skeletonFrameIds[0]}"]`)
    ) {
      this.mountMissingSectionFrames(skeletonFrameIds[0]);
    }

    form.dataset.turboFrame = turboFrame;
    this.element.dataset.ccaiFilterSkeletonFrameIds =
      JSON.stringify(skeletonFrameIds);
  }

  mountMissingSectionFrames(activeFrameId) {
    const results = this.element.querySelector("[data-ccai-results]");
    if (!results) return;

    results.replaceChildren();
    ["early", "late"].forEach((timing) => {
      const frameId =
        timing === "early"
          ? this.earlyResultsFrameIdValue
          : this.lateResultsFrameIdValue;
      const frame = document.createElement("turbo-frame");
      frame.id = frameId;
      frame.className = "block w-full";
      frame.setAttribute(
        "data-educator-home-page--table-frame-target",
        "frame",
      );
      frame.setAttribute(
        "data-action",
        "turbo:frame-load->educator-home-page--table-frame#tableLoaded",
      );
      frame.setAttribute("data-controller", "educator-home-page--skeleton-wait");
      const useSkeleton = frameId === activeFrameId;
      const template = this.element.querySelector(
        useSkeleton
          ? `template[data-ccai-section="${timing}"][data-educator-home-page--table-frame-target="skeletonTemplate"]`
          : `template[data-ccai-section-template="${timing}"]`,
      );
      frame.innerHTML = template?.innerHTML || "";
      frame.setAttribute("aria-busy", useSkeleton ? "true" : "false");
      results.appendChild(frame);
    });
  }

  filterForm() {
    return this.element.querySelector(
      'form[action*="calibrate_curricular_action_items_card/query"]',
    );
  }

  deliveryIdsFromForm(form = this.filterForm()) {
    return form?.querySelector('[name="delivery_ids"]')?.value ?? "";
  }

  pendingSkeletonFrameIds() {
    const raw = this.element.dataset.ccaiFilterSkeletonFrameIds;
    if (!raw) {
      return [this.earlyResultsFrameIdValue, this.lateResultsFrameIdValue];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [this.earlyResultsFrameIdValue, this.lateResultsFrameIdValue];
    }
  }

  selectedIdKeyByTiming(form) {
    const early = new Set();
    const late = new Set();

    form
      ?.querySelector('[data-controller*="aquifer-ui-nested-dropdown"]')
      ?.querySelectorAll('input[type="checkbox"][data-role="option"]')
      .forEach((option) => {
        if (!option.checked || !option.value || option.value === "1") return;

        const group = option.dataset.group || "";
        if (group.endsWith("_early")) early.add(option.value);
        else if (group.endsWith("_late")) late.add(option.value);
      });

    const key = (ids) => [...ids].sort().join(",");
    return { early: key(early), late: key(late) };
  }

  disconnect() {
    this.element.removeEventListener(
      "turbo:frame-load",
      this.boundScheduleSyncMultilineMetadataBadges,
    );
    if (this.metadataBadgeSyncFrame) {
      cancelAnimationFrame(this.metadataBadgeSyncFrame);
      this.metadataBadgeSyncFrame = null;
    }
    this.metadataBadgeResizeObserver?.disconnect();
    document.removeEventListener(SELECTION_CHANGED, this.boundSelectionChanged);
    this.element.removeEventListener(
      "turbo:submit-start",
      this.boundFilterSubmit,
    );
    if (this.filterFormSubmitListenerForm) {
      this.filterFormSubmitListenerForm.removeEventListener(
        "submit",
        this.boundPrepareFilterSubmitSync,
        true,
      );
      this.filterFormSubmitListenerForm = null;
    }

    connectedInstanceCount = Math.max(0, connectedInstanceCount - 1);
    if (connectedInstanceCount === 0) {
      selectedKeys.clear();
      if (boundBeforeCache) {
        document.removeEventListener("turbo:before-cache", boundBeforeCache);
        boundBeforeCache = null;
      }
    }
  }

  checkboxTargetConnected(checkbox) {
    const key = selectionKey(checkbox);
    if (key) {
      if (checkbox.checked) {
        selectedKeys.add(key);
      } else {
        checkbox.checked = selectedKeys.has(key);
      }
    }
    this.updateButtonState();
  }

  checkboxTargetDisconnected() {
    this.updateButtonState();
  }

  checkboxChanged(event) {
    const checkbox = event?.target;
    if (checkbox?.type === "checkbox") {
      this.rememberSelection(checkbox);
      document.dispatchEvent(new CustomEvent(SELECTION_CHANGED));
      return;
    }

    this.updateButtonState();
  }

  handleCheckboxKeydown(event) {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    event.currentTarget.checked = !event.currentTarget.checked;
    event.currentTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  rememberSelection(checkbox) {
    const key = selectionKey(checkbox);
    if (!key) return;

    if (checkbox.checked) {
      selectedKeys.add(key);
    } else {
      selectedKeys.delete(key);
    }
  }

  applySharedSelection() {
    this.checkboxTargets.forEach((checkbox) => {
      const key = selectionKey(checkbox);
      if (!key) return;
      checkbox.checked = selectedKeys.has(key);
    });
    this.updateButtonState();
  }

  clearSelectionOnFilterSubmit(event) {
    const form = event.target;
    if (form?.tagName !== "FORM") return;

    const action = form.getAttribute("action") || "";
    if (!action.includes("calibrate_curricular_action_items_card/query")) return;

    selectedKeys.clear();
    document.dispatchEvent(new CustomEvent(SELECTION_CHANGED));
  }

  updateButtonState() {
    const anyChecked = this.checkboxTargets.some(
      (checkbox) => checkbox.checked,
    );

    if (this.hasCreateCourseButtonTarget) {
      this.createCourseButtonTarget.disabled = !anyChecked;
    }

    if (this.hasExportButtonTarget) {
      this.exportButtonTarget.disabled = !anyChecked;
    }
  }

  uniqueSelectedCheckboxes() {
    const seen = new Set();
    const selected = [];

    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.checked) return;

      const key = selectionKey(checkbox) || checkbox.dataset.documentId;
      if (!key || seen.has(key)) return;

      seen.add(key);
      selected.push(checkbox);
    });

    return selected;
  }

  async createCourse(event) {
    event.preventDefault();

    const selectedIds = this.uniqueSelectedCheckboxes()
      .map((checkbox) => checkbox.dataset.documentId)
      .filter(Boolean);

    if (selectedIds.length === 0) {
      window.alert("Please select at least one item.");
      return;
    }

    const createButton = this.hasCreateCourseButtonTarget
      ? this.createCourseButtonTarget
      : null;
    if (createButton) createButton.disabled = true;

    try {
      const response = await fetch(this.createCourseUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ item_ids: selectedIds }),
      });

      if (!response.ok) {
        window.alert("An error occurred.");
        return;
      }

      const data = await response.json();
      if (data.success) {
        this.showConfirmationAndRedirect(
          data.document_set_id,
          data.document_set_name,
        );
        return;
      }

      window.alert(data.error || "An error occurred.");
    } catch {
      window.alert("An error occurred.");
    } finally {
      this.updateButtonState();
    }
  }

  setExportFields(event) {
    const selected = this.uniqueSelectedCheckboxes();
    if (selected.length === 0) {
      event.preventDefault();
      return;
    }

    if (this.hasCsrfTokenFieldTarget) {
      this.csrfTokenFieldTarget.value = this.csrfToken();
    }

    const items = selected.map((checkbox) => ({
      id: checkbox.dataset.documentId,
      dp: checkbox.dataset.deliveryPurpose,
      di: checkbox.dataset.deliveryIds,
    }));

    if (this.hasItemsFieldTarget) {
      this.itemsFieldTarget.value = JSON.stringify(items);
    }

    if (this.hasDeliveryIdsFieldTarget) {
      const deliveryIds =
        this.selectedDeliveryIdsValue.length > 0
          ? this.selectedDeliveryIdsValue
          : [
              ...new Set(
                selected
                  .flatMap((checkbox) =>
                    (checkbox.dataset.deliveryIds || "").split(","),
                  )
                  .filter(Boolean),
              ),
            ];

      this.deliveryIdsFieldTarget.value = JSON.stringify(
        deliveryIds.map(String),
      );
    }
  }

  showConfirmationAndRedirect(documentSetId, documentSetName) {
    this.showConfirmation(`Course created: ${documentSetName}`, {
      redirectUrl: `/document_sets/${documentSetId}/edit`,
    });
  }

  showConfirmation(message, { redirectUrl = null } = {}) {
    document.querySelector("[data-ccai-create-course-success-toast]")?.remove();

    if (!this.hasSuccessAlertTemplateTarget) return;

    const flashStack = document.getElementById("flash-messages");
    if (!flashStack) return;

    const fragment = this.successAlertTemplateTarget.content.cloneNode(true);
    const messageEl = fragment.querySelector(
      "[data-ccai-create-course-success-message]",
    );
    if (messageEl) messageEl.textContent = message;

    const toast = fragment.firstElementChild;
    if (!toast) return;

    flashStack.appendChild(toast);

    window.setTimeout(() => {
      toast.remove();
      if (redirectUrl) {
        window.open(redirectUrl, "_blank");
      }
    }, 1500);
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }
}
